import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Filter, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate, truncate } from "@/lib/utils";
import { adminFetch } from "@/lib/api";
import { useFetch } from "@/hooks/use-fetch";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";
import { StatusBadge } from "@/components/status-badge";
import { Pagination } from "@/components/pagination";
import { ConfirmModal } from "@/components/confirm-modal";

interface Contact {
  id: string;
  name: string;
  email: string;
  phone?: string;
  program?: string;
  message: string;
  status: string;
  created_at: string;
}

interface ContactsResponse {
  data: Contact[];
  pagination: {
    page: number;
    totalPages: number;
    total: number;
  };
}

const statusOptions = [
  { value: "", label: "Tüm Durumlar" },
  { value: "new", label: "Yeni" },
  { value: "read", label: "Okundu" },
  { value: "replied", label: "Yanıtlandı" },
  { value: "completed", label: "Tamamlandı" },
];

export function FormsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("limit", "20");
  if (statusFilter) queryParams.set("status", statusFilter);
  if (search) queryParams.set("search", search);

  const { data, loading, error, refetch } = useFetch<ContactsResponse>(
    `/contacts?${queryParams.toString()}`,
    [page, statusFilter, search]
  );

  async function handleDeleteContact() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminFetch(`/contacts/${deleteTarget.id}`, { method: "DELETE" });
      setDeleteTarget(null);
      refetch();
    } catch {
      // Hata durumu
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="İsim veya e-posta ara..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-9 w-full rounded-lg border border-border bg-secondary pl-9 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent sm:w-72"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {loading ? (
          <PageLoading />
        ) : error ? (
          <ErrorMessage message={error} onRetry={refetch} className="m-4" />
        ) : !data || data.data.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            Henüz iletişim formu bulunmuyor.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Ad
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      E-posta
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground md:table-cell">
                      Program
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Durum
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground sm:table-cell">
                      Tarih
                    </th>
                    <th className="w-10 px-2 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.data.map((contact) => (
                    <tr
                      key={contact.id}
                      onClick={() => navigate(`/forms/${contact.id}`)}
                      className={cn(
                        "group cursor-pointer transition-colors hover:bg-secondary/50",
                        (!contact.status || contact.status === "new") && "bg-accent/[0.03]"
                      )}
                    >
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-foreground">
                          {contact.name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-muted-foreground">
                          {contact.email}
                        </span>
                      </td>
                      <td className="hidden px-4 py-3 md:table-cell">
                        <span className="text-sm text-muted-foreground">
                          {contact.program
                            ? truncate(contact.program, 30)
                            : "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={contact.status} />
                      </td>
                      <td className="hidden px-4 py-3 sm:table-cell">
                        <span className="text-sm text-muted-foreground">
                          {formatDate(contact.created_at)}
                        </span>
                      </td>
                      <td className="px-2 py-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(contact);
                          }}
                          className="rounded-lg p-1.5 text-muted-foreground opacity-0 transition-all hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                          title="Sil"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.pagination.page}
              totalPages={data.pagination.totalPages}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      <ConfirmModal
        open={!!deleteTarget}
        title="Formu Sil"
        description={deleteTarget ? `"${deleteTarget.name}" tarafından gönderilen formu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.` : ""}
        confirmLabel="Sil"
        cancelLabel="İptal"
        variant="danger"
        loading={deleting}
        onConfirm={handleDeleteContact}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
